import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import _ from 'underscore'
import moment from 'moment'

import EditIcon from '@mui/icons-material/Edit'

import EditPremiumFeatureModal from './EditPremiumFeatureModal'

import useAutoFocus from 'hooks/useAutoFocus'
import usePremiumFeatures from 'hooks/usePremiumFeatures'

import Table from 'components/Table'

import { shortDateTime } from 'helpers/formatters'

import { color } from 'styles/colors'
import { screenSize, PageHeader, PageTitle, SinglePanePage, LoadingSinglePanePage } from 'styles/shared'
import { PremiumFeature } from 'types/PremiumFeature'
import PremiumAdoptionCard from 'pages/paywall/PremiumAdoptionCard'

const PremiumFeatures = () => {
  const { premiumFeatures, arePremiumFeaturesLoaded, setPremiumFeatures } = usePremiumFeatures()
  const [showPremiumFeatureModal, setShowPremiumFeatureModal] = useState(false)
  const [selectedPremiumFeature, setSelectedPremiumFeature] = useState(null)

  const handlePremiumFeatureSuccess = (premiumFeature: PremiumFeature) => {
    setPremiumFeatures(premiumFeatures.map((pf) => {
      if (pf.id === premiumFeature.id) {
        return premiumFeature
      }
      return pf
    }))
    handlePremiumFeatureClose()
  }

  const handlePremiumFeatureClose = () => {
    setSelectedPremiumFeature(null)
    setShowPremiumFeatureModal(false)
  }

  const handlePremiumFeatureEdit = (premiumFeature) => {
    setSelectedPremiumFeature(premiumFeature)
    setShowPremiumFeatureModal(true)
  }

  const title = useRef(null)
  useAutoFocus({ element: title })

  if (!arePremiumFeaturesLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <>
      <PageHeader>
        <PageTitle ref={title}>Premium Features</PageTitle>
      </PageHeader>
      <S.SinglePanePage>
        <Table
          items={premiumFeatures}
          noDataMessage={{
            title: 'No Premium Features',
            description: 'Premium features must be added by a developer'
          }}
        >
          <S.TableHeader>
            <S.HeaderCell title="Name" />
            <S.HeaderCell title="Description" />
            <S.HeaderCell title="Access" />
            <S.HeaderCell title="Published" />
          </S.TableHeader>

          {premiumFeatures.sort((a, b) => a.name.localeCompare(b.name)).map((premiumFeature) => (
            <Table.Row key={premiumFeature.name}>
              <S.Cell><S.Name>{premiumFeature.name}</S.Name></S.Cell>
              <S.Cell>{premiumFeature.description}</S.Cell>
              <S.Cell>
                {_.compact([
                  'Dealer',
                  premiumFeature.accessibleByAdmin && 'Admin',
                  premiumFeature.accessibleByFsr && 'FSR',
                  premiumFeature.accessibleInFreeTrial && 'Free Trial'
                ]).join(', ')}
              </S.Cell>
              <S.StatusCell>
                <div>
                  <S.Status enabled={!!premiumFeature.publishedAt}>
                    {premiumFeature.publishedAt ? 'published' : 'not published'}
                  </S.Status>
                  {premiumFeature.publishedAt && <S.Date>{shortDateTime(moment(premiumFeature.publishedAt))}</S.Date>}
                  <S.EditIcon
                    fontSize="small"
                    color="primary"
                    data-testid="edit-premium-feature"
                    onClick={() => handlePremiumFeatureEdit(premiumFeature)}
                  />
                </div>
              </S.StatusCell>
            </Table.Row>
          ))}
        </Table>
        <S.StatusCards>
          <PremiumAdoptionCard />
        </S.StatusCards>
      </S.SinglePanePage>
      {
        selectedPremiumFeature && (
          <EditPremiumFeatureModal isDisplayed={showPremiumFeatureModal} closeCallback={handlePremiumFeatureClose}
                                   premiumFeature={selectedPremiumFeature} successCallback={handlePremiumFeatureSuccess}
          />
        )
      }
    </>
  )
}

const S = {
  TableHeader: styled(Table.Header)`
      position: sticky;
      top: 15px;
  `,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
      margin-top: 0px;
    }
  `,
  HeaderCell: styled(Table.HeaderCell)`
      flex: 1;
  `,
  TableRow: styled(Table.Row)`
      align-items: center;
  `,
  Cell: styled(Table.Cell)`
      flex: 1;
      flex-direction: column;
      line-height: 24px;
      text-overflow: ellipsis;
      padding: 20px 15px;
      overflow: hidden;

      @media ${screenSize.small} {
          display: inline-block;
          width: 100%;
          padding: 5px;
          line-height: 20px;
      }
  `,
  Name: styled.span`
      overflow-wrap: break-word;
      font-weight: bold;
  `,
  StatusCell: styled(Table.Cell)`
    flex: 1;
    flex-direction: column;
    padding: 20px 15px;

    @media ${screenSize.small} {
      display: inline-block;
      width: 100%;
      padding: 5px;
      line-height: 20px;
    }
  `,
  Status: styled.div.withConfig({
    shouldForwardProp: (prop) => !['enabled'].includes(prop)
  })<{ enabled: boolean }>`
      display: inline-block;
      padding: 4px 10px;
      margin-bottom: 5px;
      text-align: center;
      font-size: 12px;
      letter-spacing: 0;
      text-transform: uppercase;
      border: 1px solid ${color('border')};
      border-radius: 14px;
      margin-right: 10px;

      ${({ enabled }) => {
    if (enabled) {
      return `
              border-color: ${color('siteMessageEnabledStatusBorder')};
              background-color: ${color('positiveStatus')};
              color: ${color('siteMessageStatusEnabledText')};
              `
    } else {
      return `
              color: ${color('siteMessageStatusDisabledText')};
            `
    }
  }}
  `,
  Date: styled.span`
      display: inline-block;
      margin-right: 10px;
  `,
  EditIcon: styled(EditIcon)`
      color: ${color('EditIcon')};
      position: relative;
      top: 4px;
      cursor: pointer;
  `,
  StatusCards: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;

    @media ${screenSize.medium} {
      grid-template-columns: 1fr;
    }
  `
}

export default PremiumFeatures
